import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, ChevronDown, ChevronUp, CheckCircle2, Circle, Send, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { readingSession } from "@/lib/session";
import TakeHomeReport from "@/components/TakeHomeReport";
import AdornmentsSection from "@/components/AdornmentsSection";
import MysticMarkings from "@/components/MysticMarkings";
import ShareMark from "@/components/ShareMark";
import { HANDS_GRATITUDE, PALMISTRY_RESPECT } from "@/lib/lore";
import { lineColor, lineInk } from "@/lib/lineColors";

export default function Reveal() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isPortal = params.get("portal") === "1";
  const homePath = isPortal ? "/portal" : "/practitioner";

  const draft = readingSession.get();
  const [lines, setLines] = useState(draft?.reading_lines ?? []);
  const [expanded, setExpanded] = useState(draft?.reading_lines?.[0]?.key ?? null);
  const [clientName, setClientName] = useState("");
  const [showReport, setShowReport] = useState(false);
  const saved = useRef(false);

  // Save the reading once on mount (for the practitioner's history).
  useEffect(() => {
    if (!draft || saved.current) return;
    saved.current = true;
    (async () => {
      const payload = {
        mode: draft.mode,
        hands: draft.online ? draft.hands : draft.hands.map((h) => ({ label: h.label, photo: h.photo })),
        reading_lines: draft.reading_lines,
        compatibility: draft.compatibility,
        markings: draft.markings,
        overall: draft.overall,
        online: draft.online,
      };
      try {
        await api.post("/readings", payload);
      } catch {
        toast.error("Could not save the reading");
      }
    })();
  }, []);

  if (!draft) {
    return (
      <div className="parch-texture flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-parch-ink">No reading in progress.</p>
        <button onClick={() => navigate(homePath, { replace: true })} className="font-medium text-parch-accent">
          Return to ritual
        </button>
      </div>
    );
  }

  const selectSnippet = (key, note) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, note } : l)));

  const openReport = () => setShowReport(true);
  const finishReading = () => navigate(homePath, { replace: true });

  const prints = draft.online
    ? draft.hands.map((h) => ({ label: h.label, uri: `data:image/png;base64,${h.ink_print}`, ink: true }))
    : draft.hands.map((h) => ({ label: h.label, uri: `data:image/jpeg;base64,${h.photo}`, ink: false }));

  return (
    <div className="parch-texture min-h-screen pb-32">
      <div className="mx-auto max-w-3xl px-6 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(homePath, { replace: true })}
            data-testid="reveal-close"
            className="text-parch-inkSoft hover:text-parch-ink"
          >
            <X size={24} />
          </button>
          {!draft.online && (
            <span className="flex items-center gap-1.5 rounded-full bg-[#E3D6B8] px-3 py-1 text-xs font-semibold text-parch-accent">
              <WifiOff size={12} /> Guided
            </span>
          )}
        </div>

        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-parch-accent">// the sacred print</p>
        <h1 className="mt-1 font-display text-6xl italic leading-tight text-parch-ink">A reading unfolds.</h1>

        {/* Ink prints */}
        <div className="mt-6 flex gap-4 overflow-x-auto pb-2">
          {prints.map((p, i) => (
            <div key={i} className="flex shrink-0 flex-col items-center gap-2">
              <div className="relative flex h-72 w-56 items-center justify-center overflow-hidden rounded-xl border border-parch-line bg-parch-card">
                <img src={p.uri} alt={p.label} className="h-full w-full object-contain" />
                {!p.ink && <div className="absolute inset-0 bg-[rgba(32,24,14,0.28)]" />}
              </div>
              <p className="text-sm font-semibold text-parch-inkSoft">{p.label}</p>
            </div>
          ))}
        </div>

        {draft.overall && (
          <p className="mt-6 font-display text-xl italic leading-relaxed text-parch-ink">{draft.overall}</p>
        )}

        {/* Guided reflection */}
        <h2 className="mb-4 mt-10 font-display text-3xl text-parch-ink">Your reading</h2>
        <div className="space-y-3">
          {lines.map((ln) => {
            const open = expanded === ln.key;
            const color = lineColor(ln.key);
            const noteColor = lineInk(ln.key);
            return (
              <div
                key={ln.key}
                className="rounded-xl border border-parch-line bg-parch-card p-5"
                style={{ borderLeftWidth: 5, borderLeftColor: color }}
              >
                <button
                  onClick={() => setExpanded(open ? null : ln.key)}
                  data-testid={`line-${ln.key}`}
                  className="flex w-full items-center gap-3 text-left"
                >
                  <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                  <div className="flex-1">
                    <p className="font-display text-xl" style={{ color }}>{ln.title}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">{ln.subtitle}</p>
                  </div>
                  {open ? <ChevronUp size={20} className="text-parch-inkSoft" /> : <ChevronDown size={20} className="text-parch-inkSoft" />}
                </button>
                <p className="mt-3 leading-relaxed" style={{ color: noteColor }}>{ln.note}</p>
                {open && (
                  <div className="mt-4 space-y-2 border-t border-parch-line pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">
                      Some of these might feel resonant — sit with what lands
                    </p>
                    {(ln.options || []).map((opt, i) => {
                      const active = opt === ln.note;
                      return (
                        <button
                          key={i}
                          onClick={() => selectSnippet(ln.key, opt)}
                          data-testid={`snippet-${ln.key}-${i}`}
                          className={`flex w-full items-start gap-2 rounded-lg p-3 text-left text-sm leading-relaxed text-parch-ink transition-colors ${
                            active ? "bg-[#E4D3AC]" : "bg-[#EFE6D2] hover:bg-[#E9DCC0]"
                          }`}
                        >
                          {active ? (
                            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-parch-accent" />
                          ) : (
                            <Circle size={16} className="mt-0.5 shrink-0 text-parch-line" />
                          )}
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {draft.compatibility && (
            <div className="rounded-xl border border-parch-line bg-[#E9DCC0] p-5">
              <p className="font-display text-xl text-parch-ink">A Reflection on Resonance</p>
              <p className="mt-3 leading-relaxed text-parch-ink">{draft.compatibility}</p>
            </div>
          )}
        </div>

        <MysticMarkings markings={draft.markings} />

        {draft.markings?.witch && (
          <div className="mt-4">
            <ShareMark markings={draft.markings} printUri={prints[0]?.uri} guestName={clientName} overall={draft.overall} />
          </div>
        )}

        <AdornmentsSection />

        {/* Grounding — the hands that carried you */}
        <div className="mt-10 rounded-xl border border-parch-line bg-parch-card p-6" data-testid="hands-gratitude">
          <p className="font-display text-2xl text-parch-ink">{HANDS_GRATITUDE.title}</p>
          <p className="mt-2 leading-relaxed text-parch-ink">{HANDS_GRATITUDE.text}</p>
        </div>
        <div className="mt-3 rounded-xl border border-parch-line bg-[#E9DCC0] p-5" data-testid="palmistry-respect">
          <p className="font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">// {PALMISTRY_RESPECT.title}</p>
          <p className="mt-2 leading-relaxed text-parch-ink">{PALMISTRY_RESPECT.text}</p>
        </div>

        {/* Take-home keepsake */}
        <h2 className="mb-2 mt-10 font-display text-3xl text-parch-ink">The take-home keepsake</h2>
        <p className="mb-4 text-sm text-parch-inkSoft">
          Add your name to personalize it. Take your reading with you — keep it on screen, copy the
          text, or download it as an image or PDF.
        </p>
        <div className="space-y-3">
          <input
            placeholder="Your name (optional)"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            data-testid="client-name-input"
            className="w-full rounded-xl border border-parch-line bg-parch-card px-4 py-3.5 text-parch-ink outline-none placeholder:text-parch-inkSoft focus:border-parch-accent"
          />
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-parch-line bg-parch-bg px-6 py-4">
        <div className="mx-auto max-w-3xl">
          <button
            onClick={openReport}
            data-testid="send-report-button"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-parch-ink py-4 font-bold text-parch-bg hover:opacity-90"
          >
            <Send size={18} />
            Reveal the take-home keepsake
          </button>
        </div>
      </div>

      <TakeHomeReport
        open={showReport}
        onClose={() => setShowReport(false)}
        onFinish={finishReading}
        guestName={clientName}
        prints={prints}
        lines={lines}
        markings={draft.markings}
        overall={draft.overall}
        compatibility={draft.compatibility}
      />
    </div>
  );
}
